import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { useDataSource } from '@/src/renderer/lib/dataSource'
import type { SubtaskTreeRow, TaskReminder } from '@/src/renderer/lib/db/models'
import { createRowDataStore, type RowData, type RowDataStore } from './rowDataStore'

const RowDataContext = createContext<RowDataStore | null>(null)

export interface TaskRowDataScopeProps {
  /** Die Aufgaben der Liste — auch die eingeklappter Abschnitte, damit Aufklappen nichts nachlädt. */
  taskIds: readonly string[]
  children: React.ReactNode
}

/**
 * **Die Zeilenabfragen einer ganzen Liste in drei Abos** (#303).
 *
 * Ohne ihn hängt jede Zeile drei eigene Live-Abfragen ein (Checkliste, Kommentarzahl, Erinnerungen) — bei
 * 150 Zeilen rund 450, und die Checkliste lief bei JEDEM Schreiben einer Aufgabe für jede Zeile neu. Der
 * Bereich fragt einmal für alle und legt das Ergebnis in einen Speicher, aus dem jede Zeile nur ihren
 * eigenen Schlüssel liest. Ein Context mit der ganzen Karte ginge nicht: Jede neue Karte ließe alle Zeilen
 * neu rendern, auch memoisierte (Memory „Performance-Fallen“, Punkt 2).
 *
 * **Verschachtelt** (die Liste „Geschafft“ steht im Board des Projekts) fragt ein innerer Bereich nur nach den
 * Aufgaben, die der äußere nicht kennt, und reicht die übrigen an ihn weiter. Zeilen ohne Bereich
 * (Aufgaben-Details, Schnell-Erfassung, Stories) fragen wie bisher selbst.
 *
 * @example
 * <TaskRowDataScope taskIds={tasks.map(t => t.id)}>
 *   {tasks.map(task => <TaskEntry key={task.id} task={task} … />)}
 * </TaskRowDataScope>
 */
export function TaskRowDataScope({ taskIds, children }: TaskRowDataScopeProps) {
  const outer = useContext(RowDataContext)
  // Immer derselbe Baum, auch wenn nichts übrig bleibt: Ein Wechsel auf ein Fragment baute die Liste neu auf.
  const own = outer ? taskIds.filter(id => outer.get(id) === undefined) : taskIds
  return <OwnScope taskIds={own} outer={outer}>{children}</OwnScope>
}

function OwnScope({ taskIds, outer, children }: TaskRowDataScopeProps & { outer: RowDataStore | null }) {
  const [store] = useState(createRowDataStore)
  const source = useDataSource()
  // Dieselbe Menge, dieselbe Referenz: Sonst zählte jede neue Liste als neue Lieferung.
  const key = [...new Set(taskIds)].sort().join(',')
  const ids = useMemo(() => (key ? key.split(',') : []), [key])
  const subtaskTrees = source.useSubtaskTrees(ids)
  const commentCounts = source.useCommentCounts(ids)
  const reminders = source.useRemindersByTask(ids)
  store.update({ ids, subtaskTrees, commentCounts, reminders })
  useLayoutEffect(() => { store.flush() })
  const chained = useMemo(() => (outer ? chain(store, outer) : store), [store, outer])
  return <RowDataContext.Provider value={chained}>{children}</RowDataContext.Provider>
}

/** Erst der eigene Speicher, dann der äußere — gemeldet wird aus beiden. */
function chain(own: RowDataStore, outer: RowDataStore): RowDataStore {
  return {
    ...own,
    get: taskId => own.get(taskId) ?? outer.get(taskId),
    subscribe(taskId, listener) {
      const stopOwn = own.subscribe(taskId, listener)
      const stopOuter = outer.subscribe(taskId, listener)
      return () => { stopOwn(); stopOuter() }
    },
  }
}

/** Der Teil dieser Aufgabe aus dem umgebenden Bereich; `undefined` ohne Bereich oder wenn sie nicht dazugehört. */
function useRowData(taskId: string | undefined): RowData | undefined {
  const store = useContext(RowDataContext)
  const subscribe = useCallback(
    (listener: () => void) => (store && taskId ? store.subscribe(taskId, listener) : () => {}),
    [store, taskId],
  )
  return useSyncExternalStore(subscribe, () => (store && taskId ? store.get(taskId) : undefined))
}

/*
  Der Einzel-Hook läuft immer mit — Hooks dürfen nicht bedingt stehen —, bekommt im Bereich aber `undefined` und
  liest dann nichts. Dexie rendert bei gleichem Ergebnis nicht neu, er kostet also nichts.
*/

/** Die Checkliste als Baum, wie `useSubtaskTree` — im Bereich aus dessen Bündel. */
export function useRowSubtaskTree(taskId: string | undefined): SubtaskTreeRow[] | undefined {
  const row = useRowData(taskId)
  const own = useDataSource().useSubtaskTree(row ? undefined : taskId)
  return row ? row.subtaskTree : own
}

/** Die Zahl lebender Kommentare, wie `useCommentCount` — im Bereich aus dessen Bündel. */
export function useRowCommentCount(taskId: string | undefined): number {
  const row = useRowData(taskId)
  const own = useDataSource().useCommentCount(row ? undefined : taskId)
  return row ? row.commentCount : own
}

/** Die lebenden Erinnerungen, wie `useTaskReminders` — im Bereich aus dessen Bündel. */
export function useRowReminders(taskId: string | undefined): TaskReminder[] | undefined {
  const row = useRowData(taskId)
  const own = useDataSource().useTaskReminders(row ? undefined : taskId)
  return row ? row.reminders : own
}
